import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { CollectiveManagementSocietyViewStore } from "../../store";
import { CollectiveManagementSocietyView } from "../view/CollectiveManagementSocietyView";
import { CollectiveManagementSocietyUpdateStore } from "../../store";
import { CollectiveManagementSocietyUpdate } from "../update/CollectiveManagementSocietyUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type CollectiveManagementSocietyModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const CollectiveManagementSocietyModalContent: React.FC<CollectiveManagementSocietyModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new CollectiveManagementSocietyUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new CollectiveManagementSocietyViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <CollectiveManagementSocietyView
            collectiveManagementSocietyViewStore={
              storeInfo.store as CollectiveManagementSocietyViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <CollectiveManagementSocietyUpdate
            collectiveManagementSocietyUpdateStore={
              storeInfo.store as CollectiveManagementSocietyUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
